import type { PreparationContent } from "./preparation-studio.js";

export const businessTopics = [
  ["companyName", "Empresa", false, "text"], ["businessArea", "Ramo", false, "text"],
  ["brandSummary", "Resumo da empresa", true, "text"], ["operationalDescription", "Como a empresa trabalha", true, "text"],
  ["offers", "Serviços e ofertas", true, "offers"], ["services", "Serviços já registrados", false, "list"], ["serviceRegion", "Regiões atendidas", true, "text"],
  ["businessHours", "Horários", false, "text"], ["contactChannels", "Canais de contato", true, "channels"],
  ["targetAudience", "Público", true, "text"], ["differentiators", "Diferenciais", true, "text"],
  ["paymentMethods", "Formas de pagamento", true, "list"], ["commercialPolicies", "Regras comerciais", true, "text"],
  ["businessObjectives", "Objetivo do atendimento", false, "text"], ["assistantName", "Nome da assistente", true, "text"],
  ["assistantRole", "Função da assistente", true, "text"], ["tone", "Tom de voz", true, "tone"],
  ["openingMessage", "Mensagem de boas-vindas", true, "text"], ["qualificationData", "Informações a coletar", true, "list"],
  ["conversionGoals", "Próximo passo desejado", true, "list"], ["humanHandoff", "Encaminhamento à equipe", false, "text"],
  ["attendanceRules", "Cuidados no atendimento", false, "text"], ["faqs", "Dúvidas frequentes", false, "faqs"],
  ["commercialInfo", "Informações comerciais", false, "text"], ["aiInstructions", "Orientações complementares", false, "text"],
] as const;
export type BusinessTopic = typeof businessTopics[number];
export type TopicOperationError = { field: string; message: string } | null;
export function topicOperationError(error: TopicOperationError, field: string | undefined): string {
  return error && error.field === field ? error.message : "";
}
export const essentialTopics = new Set(["companyName", "businessArea", "offers", "operationalDescription", "attendanceRules"]);
export const summaryTopicKeys = ["offers", "serviceRegion", "businessHours", "contactChannels", "targetAudience"] as const;
// This is a count of the five visible summary topics, not a completion score.
export function summaryTopicProgress(content: Partial<PreparationContent>) {
  const topics = businessTopics.filter(item => (summaryTopicKeys as readonly string[]).includes(item[0]));
  return { filled: topics.filter(item => topicSummary(topicValue(content, item))).length, total: topics.length };
}
export function preparationGuidance(content: Partial<PreparationContent>, pendingFields: readonly string[]) {
  const saved = businessTopics.filter(item => topicSummary(topicValue(content, item))).map(item => item[0]);
  const review = [...new Set(pendingFields)].length;
  const missing: string[] = [];
  if (!content.companyName?.trim()) missing.push("nome da empresa");
  if (!content.businessArea?.trim()) missing.push("ramo de atividade");
  if (!content.services?.length && !content.knowledge?.offers?.length && !content.knowledge?.operationalDescription?.trim() && !content.knowledge?.brandSummary?.trim()) missing.push("o que sua empresa oferece");
  if (!content.attendanceRules?.trim()) missing.push("cuidados no atendimento");
  const next = review ? `Você tem ${review} proposta(s) para revisar. Pode aplicar, editar ou deixar para depois.`
    : missing.length ? `Podemos continuar com ${missing[0]}. Conte à Luna do seu jeito ou escolha uma informação em Seu negócio.`
    : "Você já registrou as informações básicas. Pode conferir o rascunho, complementar detalhes ou perguntar à Luna o que ainda vale revisar.";
  return { saved: saved.length, review, missing, next };
}
export function topicValue(content: Partial<PreparationContent>, topic: BusinessTopic): unknown {
  const source = (topic[2] ? content.knowledge : content) as Record<string, unknown> | undefined;
  return source?.[topic[0]] ?? (topic[3] === "text" ? "" : []);
}
export function fieldRecorded(content: Partial<PreparationContent>, field: string): boolean {
  if (field === "offers" && content.services?.length) return true;
  const topic = businessTopics.find(item => item[0] === field);
  return Boolean(topic && topicSummary(topicValue(content, topic)));
}
// Conversational prompts are optional guidance; they do not change approval requirements.
export function nextConversationQuestion(content: Partial<PreparationContent>, pendingFields: readonly string[]): string | null {
  const questions: Array<[string, string]> = [
    ["companyName", "Como se chama sua empresa?"],
    ["businessArea", "Em que ramo sua empresa atua?"],
    ["offers", "Quais serviços ou produtos sua empresa oferece?"],
    ["serviceRegion", "Em quais regiões sua empresa atende?"],
    ["businessHours", "Quais são os dias e horários de atendimento?"],
    ["contactChannels", "Por quais canais seus clientes podem entrar em contato?"],
    ["targetAudience", "Quem são os clientes que sua empresa atende?"],
  ];
  const next = questions.find(([field]) => !pendingFields.includes(field) && !fieldRecorded(content, field));
  if (next?.[0] === "offers" && content.companyName?.trim() && content.businessArea?.trim()) {
    return `Vi que ${content.companyName.trim()} atua em ${content.businessArea.trim()}. Quais serviços ou produtos vocês oferecem?`;
  }
  return next?.[1] ?? null;
}
export function topicSummary(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (!Array.isArray(value)) return "";
  return value.map(item => typeof item === "string" ? item : item.name || [item.label, item.value].filter(Boolean).join(": ") || item.question || "").filter(Boolean).join(" · ");
}
export function updateTopic(content: Partial<PreparationContent>, topic: BusinessTopic, value: unknown): Partial<PreparationContent> {
  if (topic[0] === "offers" && Array.isArray(value)) {
    return { ...content, knowledge: { ...content.knowledge, offers: value } } as Partial<PreparationContent>;
  }
  if (topic[3] === "list" && Array.isArray(value)) value = [...new Set(value.map(item => String(item).trim()).filter(Boolean))];
  return topic[2] ? { ...content, knowledge: { ...content.knowledge, [topic[0]]: value } } as Partial<PreparationContent> : { ...content, [topic[0]]: value };
}
// Only the model's explicit question is used; ambiguous/free-form prose gets
// a neutral hint. Quick choices compose a message, never business facts.
export function replyContext(question: string, initial: boolean) {
  if (initial) return { placeholder: "Conte um pouco sobre sua empresa…", choices: [] as string[] };
  const normalized = question.toLocaleLowerCase("pt-BR");
  if (/canais|entrar em contato|entra(?:m)? em contato/.test(normalized)) return { placeholder: "Ex.: WhatsApp e Instagram", choices: ["WhatsApp", "Instagram", "Telefone", "Site"] };
  if (/horários|horário de atendimento|funcionamento/.test(normalized)) return { placeholder: "Ex.: Segunda a sexta, das 8h às 18h", choices: [] as string[] };
  return { placeholder: "Responda à Luna ou acrescente outra informação…", choices: [] as string[] };
}
